import React from "react";
import { useNavigate } from "react-router-dom";
import { FaTriangleExclamation as FaExclamationTriangle, FaCircleInfo, FaCircleCheck } from "react-icons/fa6";
import { formatTxnAmount } from "../utils/currency";
import CashoutRailPicker from "./CashoutRailPicker";
import CashoutProtectModal from "./CashoutProtectModal";
import { useCashout } from "../hooks/useCashout";
import { fmtMoney, num, type EarningsTotals } from "../utils/requestsFormat";
import type { MasspayHold, MinCashoutInfo, RefundFeesOwed } from "../types/orders";
import { minCashoutLock } from "../utils/minCashoutLock";
import { openMasspayPage, openMasspayWallet } from "../lib/masspayWallet";
import "./CashoutMasspay.css";
import "./ReferralCashout.css";
import "./CashoutSuccess.css";
import type { User } from "../types/User";
import { t, formatLocale } from "../i18n";
import { T } from "../i18n/T";

// Every cashout overlay on the Requests page and on Balance (whose Withdraw
// opens it over itself since 1.5.2 b49): the Transfer-to-Bank modal, the
// success popup, the protect-your-cashout panel, the connect-your-bank modal
// and the failure toast. Rendered outside the page's scroller (see the
// stacking note in RequestsPage). The flow itself is hooks/useCashout; this
// file is the screens. Split out of pages/RequestsPage.tsx.

type Props = {
  earningsByCur: EarningsTotals;
  setEarningsByCur: React.Dispatch<React.SetStateAction<EarningsTotals>>;
  commissionRate: number | null;
  refundFeesByCur: Record<string, RefundFeesOwed>;
  setlistPending: { count: number; total: number; currency: string | null };
  minCashoutInfo: MinCashoutInfo;
  /** MassPay talent: what is still inside the fraud hold (GET /orders/earnings). */
  masspayHold?: MasspayHold | null;
  profile: User | null;
  fetchAll: () => Promise<void>;
};


export default function CashoutFlow({
  earningsByCur,
  setEarningsByCur,
  commissionRate,
  refundFeesByCur,
  setlistPending,
  minCashoutInfo,
  masspayHold = null,
  profile,
  fetchAll,
}: Props) {
  const navigate = useNavigate();
  const {
    showCashoutModal,
    closeCashoutModal,
    selectedRailId,
    setSelectedRailId,
    feeTipOpen,
    setFeeTipOpen,
    refundFeeTipOpen,
    setRefundFeeTipOpen,
    successMessage,
    showCashoutSuccessModal,
    payoutMode,
    payoutDays,
    arrivesBy,
    dismissCashoutSuccess,
    needsOnboardingState,
    setNeedsOnboardingState,
    connectingStripe,
    handleConnectBankFromCashout,
    protectModalState,
    setProtectModalState,
    handleConfirmCashout,
    hasFunds,
    serverMinLock,
    docsNeededUrl,
    setDocsNeededUrl,
  } = useCashout({ earningsByCur, setEarningsByCur, fetchAll });

  // A talent paid through MassPay: the money goes to their MassPay wallet,
  // and money a fan paid in the last few days is still inside the fraud hold
  // (server masspayHold) — what a transfer can send now is the rest.
  const masspay = profile?.payout?.rail === "masspay";
  const heldOf = (cur: string) => (masspay ? num(masspayHold?.byCurrency?.[cur]?.total) : 0);
  const sendableByCur: EarningsTotals = masspay
    ? Object.fromEntries(Object.entries(earningsByCur).map(([cur, v]) => [cur, { ...v, total: Math.max(0, num(v?.total) - heldOf(cur)) }]))
    : earningsByCur;
  const allHeld = masspay && hasFunds && !Object.values(sendableByCur).some((v) => num(v?.total) > 0 || num(v?.referral) > 0);
  // Only currencies with money in them (founder, 1.5.2 b49 test: after a
  // switch to euros, then pounds, the sheet listed "€0.00" rows — the server
  // sends the talent's own currency even when it holds nothing).
  const hasMoney = (cur: string, v?: { total?: number; referral?: number }) => num(v?.total) > 0 || num(v?.referral) > 0 || heldOf(cur) > 0;
  const shownByCur = Object.fromEntries(Object.entries(sendableByCur).filter(([cur, v]) => hasMoney(cur, v))) as EarningsTotals;
  const unlocksOn = masspayHold?.unlocksAt
    ? new Date(masspayHold.unlocksAt).toLocaleDateString(formatLocale() ?? "en-US", { month: "short", day: "numeric" })
    : null;

  // Minimum-cashout lock for the modal: null = transfer allowed. The shared
  // client-side preview (utils/minCashoutLock); the server's own refusal wins
  // when set.
  const minLock = !hasFunds || allHeld
    ? null
    : serverMinLock || minCashoutLock({ earningsByCur: sendableByCur, minCashoutInfo });

  return (
    <>
      {successMessage && (
        <div className="cashout-success-message">{successMessage}</div>
      )}

      {/* Balance / Request History / Transfer to Bank moved off this page in the
          bottom-tab redesign: Balance + Transfer now live on the Balance tab
          (its Withdraw button re-opens the cashout modal below via router
          state), and Request History now lives in the merged More menu embedded
          in the Profile tab. The cashout modal machinery stays here because it's
          the shared cashout entry point. */}
      {showCashoutModal && (
        <div className="cashout-modal-overlay">
          <div className="cashout-modal">
            <div className="cashout-modal-header">
              <FaExclamationTriangle size={24} className="cashout-modal-icon" />
              <h2 className="cashout-modal-title">{masspay ? t("cashoutFlow.transferToMasspay") : t("cashoutFlow.transferToBank")}</h2>
            </div>
            <div className="cashout-modal-body">
              {!hasFunds ? (
                <p
                  style={{
                    textAlign: "center",
                    fontSize: "16px",
                    color: "#cdd1d8",
                  }}
                >
                  {t("cashoutFlow.youDonTHave")}
                </p>
              ) : allHeld ? (
                // Everything is still inside the MassPay hold: when, not a breakdown.
                <div className="cashout-hold-all">
                  {Object.entries(earningsByCur).filter(([cur, v]) => hasMoney(cur, v)).map(([cur, v]) => (
                    <p key={cur} className="cashout-hold-all-line">
                      {unlocksOn
                        ? t("cashoutFlow.yourAmountUnlocksOn", { amount: fmtMoney(cur, num(v?.total)), date: unlocksOn })
                        : t("cashoutFlow.yourAmountIsOnHold", { amount: fmtMoney(cur, num(v?.total)) })}
                    </p>
                  ))}
                  <p className="cashout-hold-all-sub">{t("cashoutFlow.earningsUnlockDaysAfter", { count: masspayHold?.holdDays ?? 7 })}</p>
                </div>
              ) : (
                <>
                  {(() => {
                    // Quote the picker on the dominant currency's total —
                    // multi-currency balances are rare and the only live rail
                    // is fee-free, so one quote covers the honest case.
                    const all = (v?: { total?: number; referral?: number }) => num(v?.total) + num(v?.referral);
                    const [domCur, domVal] = Object.entries(sendableByCur).reduce(
                      (best, entry) => (all(entry[1]) > all(best[1]) ? entry : best)
                    );
                    // MassPay: the wallet card shows what lands in the wallet —
                    // the Final amount below (after our fee and owed fees),
                    // never the gross. (Stripe's rails keep their own quote.)
                    const rate = commissionRate != null ? num(commissionRate) : 0.15;
                    const domTotal = num(domVal?.total);
                    const quoted = masspay
                      ? Math.max(0, domTotal - domTotal * rate - num(refundFeesByCur[domCur]?.amount)) + num(domVal?.referral)
                      : domTotal + num(domVal?.referral);
                    return (
                      <CashoutRailPicker
                        amountCents={Math.round(quoted * 100)}
                        currency={domCur.toLowerCase()}
                        selectedRailId={selectedRailId}
                        onSelect={setSelectedRailId}
                      />
                    );
                  })()}
                  {Object.entries(shownByCur).map(([cur, v]) => {
                    const total = num(v?.total);
                    const held = heldOf(cur);
                    // Show the talent's ACTUAL commission rate — standard 15%,
                    // or whatever the admin set for them — and split straight
                    // off it: fee = total × rate, talent keeps the rest. The
                    // card-processing fee is absorbed by the platform and is
                    // never shown here, so a standard talent always sees a clean
                    // 15% / 85% (no card-fee inflation, which used to read as
                    // much as ~30% on small bids). Older server omits the rate
                    // during a deploy → fall back to the standard 15%.
                    const rate = commissionRate != null ? num(commissionRate) : 0.15;
                    const fee = total * rate;
                    // DJ voluntary refunds: card fees owed from refunded
                    // requests come off this withdrawal (same currency only —
                    // the server deducts for real at /cashout; these are the
                    // same ledger numbers, so the modal always matches).
                    // Refer & Earn money in the balance: its own line, no fee on it.
                    const referral = num(v?.referral);
                    // Referral money alone: no request lines, and no owed fees
                    // (the server recovers those from request money only).
                    // Without referral money every line is exactly as before.
                    const referralOnly = referral > 0 && total <= 0;
                    const owedInfo = refundFeesByCur[cur];
                    const owed = referralOnly ? 0 : num(owedInfo?.amount);
                    const net = Math.max(0, total - fee - owed) + referral;
                    const feePct = +(rate * 100).toFixed(2);
                    return (
                      <div key={cur} style={{ marginBottom: 8 }}>
                        {!referralOnly && (
                        <>
                        <p className="cashout-modal-info">
                          <span>{t("cashoutFlow.total")}</span>{" "}
                          <strong>{fmtMoney(cur, total)}</strong>
                        </p>
                        <p className="cashout-modal-info">
                          <span className="fee-label">
                            {t("cashoutFlow.platformFee", { feePct })}
                            <button
                              type="button"
                              className="fee-info-btn"
                              aria-label={t("cashoutFlow.aboutThePlatformFee")}
                              aria-expanded={feeTipOpen}
                              onClick={(e) => {
                                e.stopPropagation();
                                setFeeTipOpen((p) => !p);
                              }}
                              onBlur={() => setFeeTipOpen(false)}
                            >
                              <FaCircleInfo size={13} />
                              {feeTipOpen && (
                                <span className="fee-info-tip" role="tooltip">
                                  {t("cashoutFlow.thisIsOurPlatform")}
                                </span>
                              )}
                            </button>
                          </span>{" "}
                          <strong>{fmtMoney(cur, fee)}</strong>
                        </p>
                        </>
                        )}
                        {owed > 0 && (
                          <p className="cashout-modal-info">
                            <span className="fee-label">
                              {(owedInfo?.disputeCount || 0) > 0 ? t("cashoutFlow.lostDisputesRefundFees") : t("cashoutFlow.refundCardFees")} ({owedInfo?.count || 1}):
                              <button
                                type="button"
                                className="fee-info-btn refund-fee-info-btn"
                                aria-label={t("cashoutFlow.aboutRefundCardFees")}
                                aria-expanded={refundFeeTipOpen}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRefundFeeTipOpen((p) => !p);
                                }}
                                onBlur={() => setRefundFeeTipOpen(false)}
                              >
                                <FaCircleInfo size={13} />
                                {refundFeeTipOpen && (
                                  <span className="fee-info-tip refund-fee-tip" role="tooltip">
                                    {t("cashoutFlow.comingOffThisPayoutItems", {
                                      items: (owedInfo?.items || [])
                                        .map(
                                          (it) =>
                                            t(
                                              it.kind === "dispute_share"
                                                ? "cashoutFlow.owedDisputeShare"
                                                : it.kind === "dispute_fee"
                                                ? "cashoutFlow.owedDisputeFee"
                                                : "cashoutFlow.owedRefundProcessing",
                                              { song: it.songTitle || t("cashoutFlow.aRequest") },
                                            ) +
                                            (it.refundedAt
                                              ? ` (${new Date(it.refundedAt).toLocaleDateString(formatLocale(), {
                                                  month: "short",
                                                  day: "numeric",
                                                })})`
                                              : "")
                                        )
                                        .join("; "),
                                    })}
                                  </span>
                                )}
                              </button>
                            </span>{" "}
                            <strong className="refund-fee-amount">−{fmtMoney(cur, owed)}</strong>
                          </p>
                        )}
                        {referral > 0 && (
                          <p className="cashout-modal-info cashout-referral-line">
                            <span>{t("referralCashout.referralBonus")}</span>{" "}
                            <strong>+{fmtMoney(cur, referral)}</strong>
                          </p>
                        )}
                        <p className="cashout-modal-info">
                          <span>{t("cashoutFlow.finalDjAmount")}</span>{" "}
                          <strong>{fmtMoney(cur, net)}</strong>
                        </p>
                        {held > 0 && (
                          <p className="cashout-hold-note">
                            {unlocksOn
                              ? t("cashoutFlow.amountMoreUnlocksOn", { amount: fmtMoney(cur, held), date: unlocksOn })
                              : t("cashoutFlow.amountMoreIsOnHold", { amount: fmtMoney(cur, held) })}
                          </p>
                        )}
                      </div>
                    );
                  })}
                  {minLock ? (
                    <>
                      <p className="cashout-min-note">
                        {t("cashoutFlow.transfersStartAtYou", { amount: fmtMoney(minLock.currency, minLock.minimum), amount2: fmtMoney(minLock.currency, minLock.balance) })}
                      </p>
                      <p className="cashout-min-sub">
                        {/* "Sent automatically" only while the server's auto-release runs; otherwise the waiver is a tap away. */}
                        {minCashoutInfo.autoRelease
                          ? t("cashoutFlow.sentAutomaticallyAfterDays", { amount: fmtMoney(minLock.currency, minLock.minimum), waiverDays: minLock.waiverDays })
                          : t("cashoutFlow.anythingOlderThanDays", { waiverDays: minLock.waiverDays })}
                      </p>
                    </>
                  ) : (
                    <p className="cashout-modal-note">
                      {t("cashoutFlow.beSureToAccept")}
                    </p>
                  )}
                  {setlistPending.count > 0 && (
                    <button
                      type="button"
                      className="cashout-setlist-note cashout-setlist-note--btn"
                      onClick={() => {
                        closeCashoutModal();
                        navigate("/balance?upload=1");
                      }}
                    >
                      <T k="cashoutFlow.fromPlaylistSalesNot" vars={{ amount: formatTxnAmount(setlistPending.total, setlistPending.currency || profile?.currency) }} tags={{ 0: (c) => <span className="cashout-setlist-note-chev" aria-hidden>{c}</span> }} />
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="cashout-modal-actions">
              <button
                className="cashout-cancel-btn"
                onClick={closeCashoutModal}
              >
                {t("cashoutFlow.cancel")}
              </button>
              {hasFunds && (
                <button
                  className="cashout-confirm-btn"
                  onClick={handleConfirmCashout}
                  disabled={!!minLock || allHeld}
                >
                  {t("cashoutFlow.confirm")}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showCashoutSuccessModal && (payoutMode === "masspay" || payoutMode === "masspay_pending") && (
        <div className="cashout-modal-overlay">
          <div className="cashout-modal cashout-success-popup">
            <div className="cashout-success-check">
              <FaCircleCheck size={32} />
            </div>
            <h2 className="cashout-success-title">
              {payoutMode === "masspay" ? t("cashoutFlow.sentToYourMasspayWallet") : t("cashoutFlow.onItsWayToYour")}
            </h2>
            <p className="cashout-success-sub">{t("cashoutFlow.withdrawItToYour")}</p>
            <div className="cashout-masspay-actions">
              <button
                className="cashout-confirm-btn cashout-success-ok-btn"
                onClick={() => {
                  void openMasspayWallet();
                  dismissCashoutSuccess();
                }}
              >
                {t("cashoutFlow.openMasspay")}
              </button>
              <button className="cashout-masspay-later" onClick={dismissCashoutSuccess}>
                {t("cashoutFlow.done")}
              </button>
            </div>
          </div>
        </div>
      )}

      {docsNeededUrl && (
        <div className="cashout-modal-overlay" onClick={() => setDocsNeededUrl(null)}>
          <div className="cashout-modal cashout-success-popup" onClick={(e) => e.stopPropagation()}>
            <h2 className="cashout-success-title">{t("cashoutFlow.masspayNeedsADocument")}</h2>
            <p className="cashout-success-sub">{t("cashoutFlow.uploadItAndCash")}</p>
            <div className="cashout-masspay-actions">
              <button
                className="cashout-confirm-btn cashout-success-ok-btn"
                onClick={() => {
                  openMasspayPage(docsNeededUrl);
                  setDocsNeededUrl(null);
                }}
              >
                {t("cashoutFlow.upload")}
              </button>
              <button className="cashout-masspay-later" onClick={() => setDocsNeededUrl(null)}>
                {t("cashoutFlow.later")}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCashoutSuccessModal && payoutMode !== "masspay" && payoutMode !== "masspay_pending" && (
        <div className="cashout-modal-overlay">
          <div className="cashout-modal cashout-success-popup">
            <div className="cashout-success-check">
              <FaCircleCheck size={32} />
            </div>
            <h2 className={`cashout-success-title${payoutMode === "instant" ? " cashout-success-title--solo" : ""}`}>
              {payoutMode === "instant" ? t("cashoutFlow.onItsWay30Min") : t("cashoutFlow.transferInProgress")}
            </h2>
            {/* Instant: the title already says "about 30 minutes" — no second line. */}
            {payoutMode !== "instant" && (
              <p className="cashout-success-sub">
                {payoutMode === "fallback"
                  ? t("cashoutFlow.instantWasnTAvailable", { value: arrivesBy ? t("cashoutFlow.arrivesBy", { arrivesBy }) : t("cashoutFlow.businessDays", { payoutDays }) })
                  : arrivesBy
                    ? t("cashoutFlow.arrivesByYourBank", { arrivesBy })
                    : t("cashoutFlow.businessDaysToYourBank", { count: payoutDays })}
              </p>
            )}
            <button
              className="cashout-confirm-btn cashout-success-ok-btn"
              onClick={dismissCashoutSuccess}
            >
              {t("cashoutFlow.gotItThanks")}
            </button>
          </div>
        </div>
      )}

      {protectModalState && (
        <CashoutProtectModal
          orderIds={protectModalState.orderIds}
          totals={protectModalState.totals}
          mode={protectModalState.mode || "protect"}
          onClose={() => setProtectModalState(null)}
          onProtected={() => {
            setProtectModalState(null);
            handleConfirmCashout();
          }}
        />
      )}

      {needsOnboardingState && (
        <div className="cashout-modal-overlay" onClick={() => setNeedsOnboardingState(null)}>
          <div className="cashout-modal" onClick={(e) => e.stopPropagation()}>
            <div className="cashout-modal-header">
              <h2 className="cashout-modal-title">{t("cashoutFlow.connectYourBankTo")}</h2>
            </div>
            <div className="cashout-modal-body">
              <p style={{ textAlign: "center", marginBottom: 14, fontSize: "16px", color: "#cdd1d8" }}>
                <T k="cashoutFlow.youHaveWaitingFromRequests" vars={{ amount: Object.entries(needsOnboardingState.pendingByCurrency)
                    .filter(([, v]) => v > 0)
                    .map(([cur, amt]) => fmtMoney(cur, amt))
                    .join(" + ") || "$0", count: needsOnboardingState.ordersCount }} tags={{ 0: (c) => <strong style={{ color: "#24C961", fontSize: "1.4rem", display: "inline-block", margin: "0 4px" }}>{c}</strong> }} />
              </p>
              <p style={{ textAlign: "center", color: "#9ca3af", fontSize: "0.9rem", lineHeight: 1.5 }}>
                {t("cashoutFlow.connectYourBankNow")}
              </p>
            </div>
            <div className="cashout-modal-actions">
              <button className="cashout-cancel-btn" onClick={() => setNeedsOnboardingState(null)} disabled={connectingStripe}>
                {t("cashoutFlow.later")}
              </button>
              <button
                className="cashout-confirm-btn"
                onClick={handleConnectBankFromCashout}
                disabled={connectingStripe}
              >
                {connectingStripe ? t("cashoutFlow.loading") : t("cashoutFlow.connectBank")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
